import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../api/axios";
import toast from "react-hot-toast";
import QuillEditor from "../common/QuillEditor";
import { Save, ArrowLeft, Loader2, ImagePlus } from "lucide-react";

export default function AddServicePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    titleTr: "",
    titleEn: "",
    titleAr: "",
    descriptionTr: "",
    descriptionEn: "",
    descriptionAr: "",
    image: null,
    imagePreview: "",
    isActive: true,
    sortOrder: 1,
    tags: "",
    seoTitle: "",
    seoDescription: "",
    slug: "",
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [settingsRes, serviceRes] = await Promise.all([
          api.get("/setting"),
          id ? api.get(`/service/${id}`) : Promise.resolve(null),
        ]);
        setSettings(settingsRes.data);

        if (serviceRes) {
          const d = serviceRes.data;
          setFormData((p) => ({
            ...p,
            titleTr: d.nameTr || "",
            titleEn: d.nameEn || "",
            titleAr: d.nameAr || "",
            descriptionTr: d.descriptionTr || "",
            descriptionEn: d.descriptionEn || "",
            descriptionAr: d.descriptionAr || "",
            imagePreview: d.imagePath
              ? `${process.env.REACT_APP_UPLOAD_URL}/${d.imagePath}`
              : "",
            isActive: d.isActive,
            sortOrder: d.sortOrder || 1,
            tags: d.tags || "",
            seoTitle: d.seoTitle || "",
            seoDescription: d.metaDescription || "",
            slug: d.slug || "",
          }));
        }
      } catch {
        toast.error("Veriler yüklenemedi");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const generateSlug = (title) =>
    title
      .toLowerCase()
      .replace(/ğ/g, "g")
      .replace(/ü/g, "u")
      .replace(/ş/g, "s")
      .replace(/ı/g, "i")
      .replace(/ö/g, "o")
      .replace(/ç/g, "c")
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-+|-+$/g, "");

  const handleChange = (e) => {
    const { name, value, type, files, checked } = e.target;
    const newData = {
      ...formData,
      [name]:
        type === "file" ? files[0] : type === "checkbox" ? checked : value,
    };

    if (type === "file" && files[0]) {
      const reader = new FileReader();
      reader.onload = (ev) =>
        setFormData((p) => ({ ...p, imagePreview: ev.target.result }));
      reader.readAsDataURL(files[0]);
    }

    if (name === "titleTr" || name === "titleEn") {
      const autoSlugFromOld = generateSlug(formData[name]);
      if (!formData.slug || formData.slug === autoSlugFromOld)
        newData.slug = generateSlug(value);
    }
    if ((name === "titleTr" || name === "titleEn") && !formData.seoTitle)
      newData.seoTitle = value;
    setFormData(newData);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const data = new FormData();
    data.append("NameTr", formData.titleTr);
    data.append("NameEn", formData.titleEn);
    data.append("NameAr", formData.titleAr);
    data.append("DescriptionTr", formData.descriptionTr);
    data.append("DescriptionEn", formData.descriptionEn);
    data.append("DescriptionAr", formData.descriptionAr);
    data.append("IsActive", formData.isActive);
    data.append("SeoTitle", formData.seoTitle);
    data.append("MetaDescription", formData.seoDescription || "");
    data.append("Slug", formData.slug);
    data.append("SortOrder", formData.sortOrder || 1);
    data.append("Tags", formData.tags || "");
    if (formData.image) data.append("file", formData.image);

    try {
      if (id) {
        await api.put(`/service/${id}`, data, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Hizmet güncellendi");
      } else {
        await api.post("/service", data, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Hizmet eklendi");
      }
      navigate("/service-list");
    } catch {
      toast.error("Hizmet kaydedilirken hata oluştu");
    } finally {
      setSaving(false);
    }
  };

  if (loading)
    return (
      <div className="page-loading">
        <div className="loading-spinner" />
        <p>Yükleniyor...</p>
      </div>
    );
  if (!settings)
    return (
      <div className="page-loading">
        <p>Ayarlar bulunamadı.</p>
      </div>
    );

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h2>{id ? "Hizmet Düzenle" : "Yeni Hizmet Ekle"}</h2>
          <p className="page-subtitle">
            {id ? "Mevcut hizmeti düzenleyin" : "Yeni bir hizmet oluşturun"}
          </p>
        </div>
        <button
          className="btn btn-ghost"
          onClick={() => navigate("/service-list")}
        >
          <ArrowLeft size={18} /> Listeye Dön
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="form-layout-2col">
          {/* Sol Kolon - İçerik */}
          <div className="form-main">
            {settings.turkish && (
              <div className="content-card">
                <h3 className="card-title">🇹🇷 Türkçe İçerik</h3>
                <div className="form-field">
                  <label>Başlık</label>
                  <input
                    type="text"
                    name="titleTr"
                    value={formData.titleTr}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="Türkçe başlık"
                  />
                </div>
                <div className="form-field">
                  <label>Açıklama</label>
                  <QuillEditor
                    value={formData.descriptionTr}
                    onChange={(c) =>
                      setFormData((p) => ({ ...p, descriptionTr: c }))
                    }
                    placeholder="Türkçe açıklama"
                  />
                </div>
              </div>
            )}

            {settings.english && (
              <div className="content-card">
                <h3 className="card-title">🇬🇧 English Content</h3>
                <div className="form-field">
                  <label>Title</label>
                  <input
                    type="text"
                    name="titleEn"
                    value={formData.titleEn}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="English title"
                  />
                </div>
                <div className="form-field">
                  <label>Description</label>
                  <QuillEditor
                    value={formData.descriptionEn}
                    onChange={(c) =>
                      setFormData((p) => ({ ...p, descriptionEn: c }))
                    }
                    placeholder="English description"
                  />
                </div>
              </div>
            )}

            {settings.arabic && (
              <div className="content-card">
                <h3 className="card-title">🇸🇦 المحتوى العربي</h3>
                <div className="form-field">
                  <label>العنوان</label>
                  <input
                    type="text"
                    name="titleAr"
                    value={formData.titleAr}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="العنوان"
                  />
                </div>
                <div className="form-field">
                  <label>الوصف</label>
                  <QuillEditor
                    value={formData.descriptionAr}
                    onChange={(c) =>
                      setFormData((p) => ({ ...p, descriptionAr: c }))
                    }
                    placeholder="الوصف"
                    dir="rtl"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Sağ Kolon - Ayarlar */}
          <div className="form-sidebar">
            <div className="content-card sticky-card">
              <h3 className="card-title">Görsel</h3>
              <div className="image-upload-area">
                {formData.imagePreview ? (
                  <div className="image-preview">
                    <img src={formData.imagePreview} alt="Preview" />
                    <button
                      type="button"
                      className="remove-image"
                      onClick={() =>
                        setFormData((p) => ({
                          ...p,
                          image: null,
                          imagePreview: "",
                        }))
                      }
                    >
                      ×
                    </button>
                  </div>
                ) : (
                  <label className="upload-placeholder">
                    <ImagePlus size={32} />
                    <span>Görsel Yükle</span>
                    <input
                      type="file"
                      name="image"
                      accept="image/*"
                      onChange={handleChange}
                      hidden
                    />
                  </label>
                )}
              </div>
            </div>

            <div className="content-card">
              <h3 className="card-title">Ayarlar</h3>
              <div className="form-field">
                <label>Etiketler</label>
                <input
                  type="text"
                  name="tags"
                  value={formData.tags}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="web, tasarım, seo"
                />
              </div>
              <div className="form-field">
                <label className="toggle-label">
                  <span>Aktif</span>
                  <label className="toggle">
                    <input
                      type="checkbox"
                      name="isActive"
                      checked={formData.isActive}
                      onChange={handleChange}
                    />
                    <span className="toggle-slider" />
                  </label>
                </label>
              </div>
            </div>

            <div className="content-card">
              <h3 className="card-title">SEO Ayarları</h3>
              <div className="form-field">
                <label>URL (Slug)</label>
                <input
                  type="text"
                  name="slug"
                  value={formData.slug}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="ornek-hizmet"
                />
              </div>
              <div className="form-field">
                <label>
                  SEO Başlık{" "}
                  <small className="char-count">
                    {formData.seoTitle.length}/60
                  </small>
                </label>
                <input
                  type="text"
                  name="seoTitle"
                  value={formData.seoTitle}
                  onChange={handleChange}
                  className="form-input"
                  maxLength={60}
                />
              </div>
              <div className="form-field">
                <label>
                  Meta Açıklama{" "}
                  <small className="char-count">
                    {formData.seoDescription.length}/160
                  </small>
                </label>
                <textarea
                  name="seoDescription"
                  value={formData.seoDescription}
                  onChange={handleChange}
                  className="form-input"
                  rows={3}
                  maxLength={160}
                />
              </div>
            </div>

            <button
              type="submit"
              className="btn btn-primary btn-full"
              disabled={saving}
            >
              {saving ? (
                <>
                  <Loader2 size={18} className="spin" /> Kaydediliyor...
                </>
              ) : (
                <>
                  <Save size={18} /> {id ? "Güncelle" : "Kaydet"}
                </>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
